/**
 * 量测状态同步 — 将 store measureMode 双向同步到 viewer 原生量测工具。
 *
 * - store → viewer: measureMode 变更时调用 setMeasureActive
 * - viewer → store: viewer measure:changed 事件回流到 store
 */
import { type RefObject, useEffect, useRef } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import type { RendererController } from '@renderer/index';

export function useMeasureSync(controllerRef: RefObject<RendererController | null>) {
  const measureMode = useViewerStore((s) => s.measureMode);
  const navigationMode = useViewerStore((s) => s.navigationMode);
  const unitSystem = useViewerStore((s) => s.unitSystem);
  const status = useViewerStore((s) => s.status);
  // 记录进入量测前的导航模式，退出时恢复
  const prevNavModeRef = useRef(useViewerStore.getState().navigationMode);

  // store → viewer 同步量测状态
  // 注意：viewer 内部 measure.toggle 已自动切 orbit，不需要再从 store 侧推 navigationMode，
  // 否则会与 useNavigationController 的 setNavigationMode 产生竞态。
  useEffect(() => {
    const isActive = measureMode === 'placing';

    const store = useViewerStore.getState();
    if (isActive) {
      prevNavModeRef.current = store.navigationMode;
    }

    controllerRef.current?.measure.setActive(isActive);

    // 退出量测时恢复之前的导航模式
    if (!isActive && prevNavModeRef.current && prevNavModeRef.current !== store.navigationMode) {
      store.setNavigationMode(prevNavModeRef.current);
    }
  }, [controllerRef, measureMode]);

  // store → viewer 同步单位制：仅在 viewer ready 后推送，避免 loading/error 状态调用 renderer
  useEffect(() => {
    if (status !== 'ready') return;
    controllerRef.current?.measure.setUnitSystem(unitSystem);
  }, [controllerRef, unitSystem, status]);

  // 量测激活期间用户手动切到非 orbit 模式 → 自动退出量测
  useEffect(() => {
    if (measureMode !== 'idle' && navigationMode !== 'orbit') {
      useViewerStore.getState().setMeasureMode('idle');
    }
    // 仅在 navigationMode 变化时响应（measureMode 变化不触发此逻辑）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationMode]);

  // viewer → store（viewer 内部切换量测时回流）
  useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;

    const onMeasureChanged = (active: boolean) => {
      const current = useViewerStore.getState().measureMode;
      const expected = active ? 'placing' : 'idle';
      if (current !== expected) {
        useViewerStore.getState().setMeasureMode(expected);
      }
    };

    ctrl.on('measure:changed', onMeasureChanged);
    return () => ctrl.off('measure:changed', onMeasureChanged);
  }, [controllerRef]);
}
